// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { OrganizationDefaultAssetMarker } from '@/components/organization-default-asset-marker';
import {
  AccountBalanceOutlined,
  AirplaneTicketOutlined,
  AnalyticsOutlined,
  ApartmentOutlined,
  BallotOutlined,
  ChecklistRtlOutlined,
  ExploreOutlined,
  FactCheckOutlined,
  ForkRightOutlined,
  FormatListNumberedRtlOutlined,
  HandymanOutlined,
  InventoryOutlined,
  KeyboardReturnOutlined,
  KeyboardTabOutlined,
  LocalPoliceOutlined,
  LocalShippingOutlined,
  MediationOutlined,
  PersonRounded,
  PlaceOutlined,
  QueryStatsRounded,
  ReceiptLongOutlined,
  RocketLaunchOutlined,
  SettingsOutlined,
  ShoppingCartOutlined,
  StartOutlined,
  StoreOutlined,
  SupportAgentRounded,
  TimerOutlined,
  TrackChangesOutlined,
  WidgetsOutlined,
} from '@/constants/hardfin-standard-icons';

export interface NavigationItem {
  label: string;
  description: string;
  to: string;
  icon: React.ReactElement;
  disabled?: boolean;
  dropdownChip?: string;
  navbarChip?: string;
}

/**
 * Configuration for all navigation sections in the app
 */
export const navigationSectionsConfig: {
  label: string;
  subsectionAlignment: 'left' | 'center' | 'right';
  icon: React.ReactElement;
  additionalPaths: string[];
  items: NavigationItem[];
}[] = [
  {
    label: 'Dashboards',
    subsectionAlignment: 'left',
    icon: <ExploreOutlined fontSize="small" />,
    additionalPaths: ['/dashboard'], // Include dashboard root path
    items: [
      {
        label: 'Assets',
        description:
          'Summary of registered equipment with location and status details',
        to: '/dashboard/asset',
        icon: (
          <OrganizationDefaultAssetMarker iconProps={{ fontSize: 'small' }} />
        ),
      },
      {
        label: 'Customers',
        description: 'Summary of customer accounts and activity',
        to: '/dashboard/customer',
        icon: <ApartmentOutlined fontSize="small" />,
      },
      {
        label: 'Locations',
        description:
          'Summary of geographic activity for inventory and non-inventory',
        to: '/dashboard/location',
        icon: <PlaceOutlined fontSize="small" />,
      },
    ],
  },
  {
    label: 'Setup & configure',
    subsectionAlignment: 'left',
    icon: <SettingsOutlined fontSize="small" />,
    additionalPaths: [], // All paths are covered by items
    items: [
      {
        label: 'Catalog',
        description: 'Manage devices, parts, and services',
        to: '/catalog',
        icon: <BallotOutlined fontSize="small" />,
      },
      {
        label: 'Customers',
        description: 'Set up and manage customers',
        to: '/companies/customers',
        icon: <ApartmentOutlined fontSize="small" />,
      },
      {
        label: 'Suppliers',
        description: 'Set up and manage suppliers',
        to: '/companies/suppliers',
        icon: <StoreOutlined fontSize="small" />,
      },
      {
        label: 'Locations',
        description: 'Set up locations',
        to: '/locations',
        icon: <PlaceOutlined fontSize="small" />,
      },
      {
        label: 'Contacts',
        description: 'Set up and manage company contacts',
        to: '/contacts',
        icon: <PersonRounded fontSize="small" />,
      },
    ],
  },
  {
    label: 'Buy & build',
    subsectionAlignment: 'center',
    icon: <HandymanOutlined fontSize="small" />,
    additionalPaths: [],
    items: [
      {
        label: 'Procurement',
        description:
          'Submit purchase orders (POs) for sourcing parts or inventory',
        to: '/purchase-orders',
        icon: <ShoppingCartOutlined fontSize="small" />,
      },
      {
        label: 'Receiving',
        description:
          'Track flows for receiving incoming shipments of new items',
        to: '/shipments?type=inbound',
        icon: <InventoryOutlined fontSize="small" />,
      },
      {
        label: 'Parts inventory',
        description: 'Review real-time component counts and locations',
        to: '/inventory',
        icon: <FormatListNumberedRtlOutlined fontSize="small" />,
      },
      {
        label: 'Assembly',
        description:
          'Build finished assemblies from parts and register new assets',
        to: '/assemblies/unregistered',
        icon: <MediationOutlined fontSize="small" />,
      },
    ],
  },
  {
    label: 'Plan & deploy',
    subsectionAlignment: 'center',
    icon: <RocketLaunchOutlined fontSize="small" />,
    additionalPaths: [], // All paths covered by items or their report variants
    items: [
      {
        label: 'Upcoming projects',
        description: 'Review upcoming projects, timelines, and deliverables',
        to: '/projects?statuses=%5B"upcoming"%5D',
        icon: <WidgetsOutlined fontSize="small" />,
      },
      {
        label: 'Available assets',
        description: 'Track available assets ready to be deployed',
        to: '/assets?inventoryStatus=%5B"in_inventory"%5D',
        icon: (
          <OrganizationDefaultAssetMarker iconProps={{ fontSize: 'small' }} />
        ),
      },
      {
        label: 'Fulfill',
        description:
          'Manage fulfillment orders (FOs) to plan outbound shipments',
        to: '/fulfillments',
        icon: <ChecklistRtlOutlined fontSize="small" />,
      },
      /*
      {
        label: 'Fulfillment',
        description: 'Review fulfillment statuses of projects and line items',
        to: '/reports/fulfillment',
        icon: <ChecklistRtlOutlined fontSize="small" />,
      },
      */
      {
        label: 'Ship',
        description: 'Track outbound shipments and their status',
        to: '/shipments?type=outbound',
        icon: <LocalShippingOutlined fontSize="small" />,
      },
      {
        label: 'Movements',
        description: 'Track active and completed asset movements',
        to: '/movements',
        icon: <ForkRightOutlined fontSize="small" />,
      },
      {
        label: 'Assets in transit to field',
        description: 'Monitor assets in transit to a customer',
        to: '/assets?transitStatus=%5B"in_transit_to_field"%5D',
        icon: <StartOutlined fontSize="small" />,
      },
    ],
  },
  {
    label: 'Track & manage',
    subsectionAlignment: 'center',
    icon: <TrackChangesOutlined fontSize="small" />,
    additionalPaths: [], // No additional paths
    items: [
      {
        label: 'Active projects',
        description: 'Review active projects, timelines, and deliverables',
        to: '/projects?statuses=%5B"active"%5D',
        icon: <WidgetsOutlined fontSize="small" />,
      },
      {
        label: 'Deployed assets',
        description: 'Monitor assets or parts currently deployed in the field',
        to: '/assets?inventoryStatus=%5B"not_in_inventory"%5D',
        icon: (
          <OrganizationDefaultAssetMarker iconProps={{ fontSize: 'small' }} />
        ),
      },
      {
        label: 'Time tracking',
        description:
          'Enter, update, and analyze hours for project and asset activity',
        to: '/time',
        icon: <TimerOutlined fontSize="small" />,
      },
      {
        label: 'Cost tracking',
        description:
          'Enter, update, and analyze costs for project and asset activity',
        to: '/costs',
        icon: <AirplaneTicketOutlined fontSize="small" />,
      },
    ],
  },
  {
    label: 'Support & repair',
    subsectionAlignment: 'right',
    icon: <LocalPoliceOutlined fontSize="small" />,
    additionalPaths: [], // No additional paths
    items: [
      {
        label: 'Tickets',
        description: 'Record and resolve field issues or requests',
        to: '/tickets',
        icon: <SupportAgentRounded fontSize="small" />,
      },
      {
        label: 'Returns',
        description:
          'Input return orders (ROs) to move assets back from a customer',
        to: '/return-orders',
        icon: <KeyboardReturnOutlined fontSize="small" />,
      },
      {
        label: 'Assets returning to stock',
        description: 'Monitor assets returning to stock',
        to: '/assets?transitStatus=%5B"in_transit_to_inventory"%5D',
        icon: <KeyboardTabOutlined fontSize="small" />,
      },
    ],
  },
  /*
    {
      label: 'Billing & accounting',
      subsectionAlignment: 'right',
      icon: <AccountBalanceOutlined fontSize="small" />,
      additionalPaths: [], // No additional paths
      items: [
        {
          label: 'Invoices',
          description: 'Manage invoices and update payment history',
          to: '/invoices',
          icon: <ReceiptOutlined fontSize="small" />,
          disabled: true,
          dropdownChip: 'Coming soon',
          navbarChip: 'Soon',
        },
        {
          label: 'Payments',
          description: 'Manage payments and update payment history',
          to: '/payments',
          icon: <ReceiptOutlined fontSize="small" />,
          disabled: true,
          dropdownChip: 'Coming soon',
          navbarChip: 'Soon',
        },
        {
          label: 'Costing',
          description:
            'Enter, update, and analyze costs for project and asset activity',
          to: '/costs',
          icon: <AnalyticsOutlined fontSize="small" />,
          disabled: true,
          dropdownChip: 'Coming soon',
          navbarChip: 'Soon',
        },
        {
          label: 'Asset accounting',
          description: 'Review asset-level account balances and close periods',
          to: '/reports/asset-snapshot/inventory',
          icon: <AccountBalanceOutlined fontSize="small" />,
          disabled: true,
          dropdownChip: 'Coming soon',
          navbarChip: 'Soon',
        },
        {
          label: 'Revenue accounting',
          description:
            'Set up SSPs, rev rec triggers, generate revenue schedules',
          to: '/revenue-accounting',
          icon: <AnalyticsOutlined fontSize="small" />,
          disabled: true,
          dropdownChip: 'Coming soon',
          navbarChip: 'Soon',
        },
      ],
    },
    */
  {
    label: 'Bill & report',
    subsectionAlignment: 'right',
    icon: <AnalyticsOutlined fontSize="small" />,
    additionalPaths: [], // All paths covered by items
    items: [
      {
        label: 'Billing',
        description: 'Manage plans, charges, invoices, and A/R aging',
        to: '/billing',
        icon: <ReceiptLongOutlined fontSize="small" />,
      },
      {
        label: 'Accounting',
        description: 'Manage accounting periods and journal entries',
        to: '/accounting',
        icon: <AccountBalanceOutlined fontSize="small" />,
      },
      {
        label: 'Reporting',
        description: 'Run operational and financial performance reports',
        to: '/reports',
        icon: <QueryStatsRounded fontSize="small" />,
      },
      {
        label: 'Audit logs',
        description: 'Maintain compliance with full system change history',
        to: '/audit',
        icon: <FactCheckOutlined fontSize="small" />,
      },
    ],
  },
];
